/**
 * rd-bookings — the shared client booking card (photo-left glass card), extracted
 * from the Home Upcoming list so Home (UpcomingList) and the Bookings tab render
 * the identical idiom. DOM structure + class names preserved from the original.
 *
 * Adds the Frame-3 status-pill vocabulary (via getBookingCardPill) and the
 * DEPOSIT_PAID balance chip; the card itself is one tap target → the booking
 * detail (no inline actions — those live on the detail page).
 */
import type { UpcomingBooking } from '@/types/dashboard';
import { FocalImage } from '@/components/FocalImage';
import {
  formatBalanceChip,
  formatCardDate,
  formatCardTime,
  formatLocationText,
  getBookingCardPill,
  type BookingStatusPillVariant,
} from '@/pages/BookingHubPage/homeFormat';
import { CalendarIcon, ChevronRightIcon, ClockIcon, GlobeIcon, LocationIcon, PhoneIcon, UsersIcon } from '@/pages/BookingHubPage/sections/icons';
import styles from './BookingCard.module.css';

/** All status labels share the readable accent treatment; payment keeps its clock/ring emphasis. */
const PILL_VARIANT_CLASS: Record<BookingStatusPillVariant, string> = {
  confirmed: '',
  payment: styles.statusPillPayment,
  pending: '',
  waitlist: '',
};

/** Small credit-card glyph for the balance chip (mockup payment-row icon). */
function CardIcon({ className }: { className?: string } = {}) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}

export interface BookingCardProps {
  booking: UpcomingBooking;
  onOpen: (id: string) => void;
  variant?: 'compact' | 'roomy';
}

export function BookingCard({ booking, onOpen, variant = 'compact' }: BookingCardProps) {
  const pill = getBookingCardPill(booking);
  const pillClass = PILL_VARIANT_CLASS[pill.variant];
  const hasPhoto = booking.service.imageUrl != null;
  const isEvent = booking.service.kind === 'event';
  const isRoomy = variant === 'roomy';
  const going =
    isEvent && booking.capacity != null && booking.spotsRemaining != null
      ? Math.max(0, booking.capacity - booking.spotsRemaining)
      : null;
  // Positive-only: a $0 remainder (deposit covered the price) must not render a
  // misleading "$0 due" chip even while paymentProgressStatus is DEPOSIT_PAID.
  const showBalanceChip =
    booking.paymentProgressStatus === 'DEPOSIT_PAID' &&
    booking.balanceDue != null &&
    booking.balanceDue > 0;

  const statusPill = (
    <span
      className={pillClass ? `${styles.statusPill} ${pillClass}` : styles.statusPill}
      data-testid="booking-status-pill"
    >
      {pill.variant === 'payment' && <ClockIcon />}
      {pill.label}
    </span>
  );

  return (
    <button
      type="button"
      className={isRoomy ? `${styles.bookingCard} ${styles.roomy}` : styles.bookingCard}
      onClick={() => onOpen(booking.id)}
      aria-label={`View booking: ${booking.service.name}`}
      data-variant={variant}
    >
      {hasPhoto && (
        <FocalImage
          src={booking.service.imageUrl!}
          alt=""
          className={styles.bookingImage}
          focalPointX={booking.service.focalPointX}
          focalPointY={booking.service.focalPointY}
          focalPointScale={booking.service.focalPointScale}
          data-photo
        >
          {statusPill}
        </FocalImage>
      )}
      <div className={styles.bookingBody}>
        {isEvent && (
          <div className={styles.typeKicker}>
            <UsersIcon />
            Group event
          </div>
        )}
        <div className={styles.bookingTitle} title={booking.service.name}>{booking.service.name}</div>
        {isRoomy && booking.provider && (
          <div className={styles.providerLine}>
            <span
              className={styles.providerAvatar}
              style={booking.provider.avatarUrl ? { backgroundImage: `url('${booking.provider.avatarUrl}')` } : undefined}
              aria-label={booking.provider.name}
            >
              {!booking.provider.avatarUrl && booking.provider.name.charAt(0)}
            </span>
            <span>with <b>{booking.provider.name}</b></span>
          </div>
        )}
        <div className={styles.bookingMeta}>
          <div className={`${styles.bookingMetaRow} ${styles.dateRow}`} data-date-row>
            <span className={styles.metaText}>
              <CalendarIcon />
              {isRoomy ? (
                `${formatCardDate(booking.scheduledAt)} · ${formatCardTime(booking.scheduledAt)} · ${booking.duration} min`
              ) : (
                <>
                  {formatCardDate(booking.scheduledAt)}
                  <ClockIcon />
                  {formatCardTime(booking.scheduledAt)}
                </>
              )}
            </span>
            {!hasPhoto && statusPill}
          </div>
          <div className={styles.bookingMetaRow}>
            {isRoomy && going != null ? (
              <span className={styles.eventMeta}>
                <UsersIcon />
                {going} going
              </span>
            ) : booking.location.isByPhone ? (
              <PhoneIcon />
            ) : booking.location.isVirtual ? (
              <GlobeIcon />
            ) : (
              <LocationIcon />
            )}
            <span className={styles.locationText}>
              {isRoomy && going != null ? '· ' : ''}{formatLocationText(booking.location)}
            </span>
            {showBalanceChip && (
              <span className={styles.paymentRow}>
                <CardIcon />
                {formatBalanceChip(
                  booking.balanceDue as number,
                  booking.balanceCollectionMethod,
                  booking.balanceDueBy,
                )}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.chevronSlot}>
        <ChevronRightIcon className={styles.bookingChev} />
      </div>
    </button>
  );
}
